import { defaultTo, mergeDeepRight } from "ramda";

const PrimaryTypeBase = {
  text: "text-color-white",
  background: "bg-color-blue",
};

const SecondaryTypeBase = {
  text: "text-color-white",
  background: "bg-color-red",
};

const FadedTypeBase = {
  root: "widget widget__transparent",
  text: "text-color-white",
};

const actionGroup = {
  root: "d-flex flex-wrap flex-column justify-content-start align-items-start",
};

const baseWrapperClasses =
  "widget--info px-6 py-5 px-md-7 d-flex flex-column justify-content-center";

const basePromoConfig = {
  default: {
    ratio: "5x4",
    wrapper: baseWrapperClasses,
    text: "text-color-blue",
    background: "",
    actions: actionGroup,
    iconProps: {
      classes: { root: "icon-container " },
    },
  },
  primary: PrimaryTypeBase,
  secondary: SecondaryTypeBase,
};

const largePromoConfig = mergeDeepRight(basePromoConfig, {
  default: {
    root: "widget widget-promo__large",
    iconProps: {
      classes: { root: "icon-container widget__icon mb-4 mb-lg-7" },
    },
    headingProps: {
      as: "h4",
      variant: "h-card",
      classes: { root: "mb-4 mb-lg-7 max-lines max-lines--1 word-break-word" },
    },
    titleProps: {
      as: "h2",
      variant: "h2-widget",
      classes: {
        root: "mb-4 mb-md-6 word-break-word max-lines max-lines--2",
      },
    },
    descriptionProps: {
      as: "p",
      variant: "p-medium",
      classes: {
        root: "d-none d-xl-block d-xl--webkit-box mb-7 mb-md-8 word-break-word max-lines max-lines--2",
      },
    },
  },
  faded: {
    ...FadedTypeBase,
    root: `${FadedTypeBase.root} widget-promo__large`,
  },
});

const widePromoConfig = mergeDeepRight(basePromoConfig, {
  default: {
    ratio: "16x9",
    root: "widget widget-promo__wide",
    iconProps: {
      classes: { root: "icon-container widget__icon mb-4 mb-md-7" },
    },
    headingProps: {
      ...largePromoConfig.default.headingProps,
    },
    titleProps: {
      as: "h2",
      variant: "h2-enriched",
      classes: {
        root: "mb-7 word-break-word max-lines max-lines--2",
      },
    },
    /** Default */
    descriptionProps: {
      ...largePromoConfig.default.descriptionProps,
    },
  },
  primary: {
    text: "text-color-white",
    background: "bg-color-blue",
  },
  secondary: {
    text: "text-color-white",
    background: "bg-color-red",
  },
  faded: FadedTypeBase,
});

const doublePromoConfig = mergeDeepRight(basePromoConfig, {
  default: {
    root: "widget widget-promo__double",
    iconProps: {
      classes: { root: "icon-container widget__icon mb-7" },
    },
    headingProps: {
      as: "h4",
      variant: "h-card",
      classes: { root: "mb-4 word-break-word max-lines max-lines--1" },
    },
    titleProps: {
      as: "h3",
      variant: "h3-pdp",
      classes: {
        root: "mb-7 mb-xl-4 word-break-word max-lines max-lines--2",
      },
    },
    descriptionProps: {
      as: "p",
      variant: "p-medium",
      classes: {
        root: "d-none d-xl-block d-xl--webkit-box mb-7 word-break-word max-lines max-lines--2",
      },
    },
  },
  faded: {
    ...FadedTypeBase,
    root: `${FadedTypeBase.root} widget-promo__double`,
  },
});

const singlePromoConfig = mergeDeepRight(basePromoConfig, {
  default: {
    ratio: "16x9 ratio-lg-none",
    root: "widget widget-promo__single",
    wrapper: "widget--info px-6 pt-6 pb-4 px-xl-7 pt-xl-7",
    actions: {
      root: "d-none d-xs-block mt-auto",
    },
    headingProps: {
      as: "h4",
      variant: "h-card",
      classes: {
        root: "d-none d-xl-block d-xl--webkit-box mb-4 max-lines max-lines--1 word-break-word",
      },
    },
    titleProps: {
      as: "h3",
      variant: "h3-pdp",
      classes: {
        root: "mb-9 word-break-word max-lines max-lines--2",
      },
    },
    /** Default */
    descriptionProps: {
      as: "p",
      variant: "p-medium",
      classes: {
        root: "d-none d-xl-block d-xl--webkit-box mb-7 mb-md-8 word-break-word max-lines max-lines--2",
      },
    },
  },
  faded: {
    root: "widget widget-promo__single widget__transparent",
    text: "text-color-white",
  },
});

const mediumPromoConfig = mergeDeepRight(basePromoConfig, {
  default: {
    root: "widget widget-promo__medium",
    wrapper: "widget--info p-6 p-md-7",
    headingProps: {
      as: "h4",
      variant: "h4-enriched",
      classes: { root: "mb-4 max-lines max-lines--1 word-break-word" },
    },
    titleProps: {
      as: "h3",
      variant: "h3-enriched",
      classes: {
        root: "mb-4 mb-md-7 word-break-word max-lines max-lines--2",
      },
    },
    descriptionProps: {
      as: "p",
      variant: "p-medium",
      classes: {
        root: "mb-7 d-none d-md-block d-md--webkit-box word-break-word max-lines max-lines--2",
      },
    },
  },
  faded: {
    ratio: "4x5",
    root: "widget widget-promo__medium widget__transparent",
    text: "text-color-white",
  },
});

const smallPromoConfig = mergeDeepRight(basePromoConfig, {
  default: {
    root: "widget widget-promo__small",
    wrapper: "widget--info p-6 p-md-7",
    headingProps: {
      as: "h4",
      variant: "h4-enriched",
      classes: { root: "mb-4 max-lines max-lines--1 word-break-word" },
    },
    titleProps: {
      as: "h3",
      variant: "h3-enriched",
      classes: {
        root: "m-0 word-break-word max-lines max-lines--2",
      },
    },
    /** Default */
    descriptionProps: {
      as: "p",
      variant: "p-medium",
      classes: {
        root: "d-none d-xl-block d-xl--webkit-box mb-7 mb-md-8 word-break-word max-lines max-lines--2",
      },
    },
  },
  primary: PrimaryTypeBase,
  secondary: SecondaryTypeBase,
  faded: {
    ratio: "4x5",
    root: "widget widget-promo__small widget__transparent",
    text: "text-color-white",
  },
});

export const getVariants = () => ({
  large: largePromoConfig,
  wide: widePromoConfig,
  double: doublePromoConfig,
  single: singlePromoConfig,
  medium: mediumPromoConfig,
  small: smallPromoConfig,
});
